import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import { SectionCard } from '@/components/ui/Card';
import { Btn } from '@/components/ui/Button';
import { roles } from '@/data';

interface PermCellProps { on: boolean }
function PermCell({ on }: PermCellProps) {
  const [checked, setChecked] = useState(on);
  return (
    <button type="button"
      className={"mp-permcell" + (checked ? " is-on" : "")}
      onClick={() => setChecked((c) => !c)}
      aria-pressed={checked}>
      <i className={"bi " + (checked ? "bi-check-lg" : "bi-dash")}></i>
    </button>
  );
}

export default function Roles() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const R = roles;
  const ln = (ko: string, mn: string) => (lang === "ko" ? ko : mn);
  const [selId, setSelId] = useState("lead");
  const sel = R.list.find((r) => r.id === selId)!;

  const grantedCount = (role: typeof R.list[number]) =>
    R.modules.reduce<number>((s, m) => s + role.matrix[m].reduce<number>((a, b) => a + b, 0), 0);
  const totalCells = R.modules.length * R.perms.length;

  return (
    <div className="mp-page" data-screen-label="역할/권한">
      <PageHeader
        crumbs={[t('home'), t('mSystem'), t('mRoles')]}
        title={t('rolesTitle')}
        titleExtra={<div className="mp-pagehead__meta"><span className="mp-pagehead__updated">{t('rolesSub')}</span></div>}
        actions={<Btn kind="primary" icon="bi-plus-lg">{t('addRole')}</Btn>}
      />

      <div className="mp-grid mp-grid--master-detail">
        {/* 역할 목록 */}
        <SectionCard flush>
          <ul className="mp-rolelist">
            {R.list.map((role) => (
              <li key={role.id}>
                <button type="button"
                  className={"mp-rolelist__item" + (role.id === selId ? " is-current" : "")}
                  onClick={() => setSelId(role.id)}>
                  <span className="mp-rolelist__bar" style={{ background: role.color }}></span>
                  <span className="mp-rolelist__body">
                    <span className="mp-rolelist__name">{ln(role.ko, role.mn)}</span>
                    <span className="mp-rolelist__members">
                      <i className="bi bi-people-fill"></i> {role.members} {t('persons')}
                    </span>
                  </span>
                  <span className="mp-rolelist__count">{grantedCount(role)}/{totalCells}</span>
                </button>
              </li>
            ))}
          </ul>
        </SectionCard>

        {/* 권한 매트릭스 */}
        <SectionCard
          title={<span>{ln(sel.ko, sel.mn)} <span className="mp-card__unit">· {t('permMatrix')}</span></span>}
          extra={
            <div className="mp-btnrow">
              <span className="mp-rolestat"><i className="bi bi-people-fill"></i> {sel.members} {t('persons')}</span>
              <Btn small icon="bi-save">{t('save')}</Btn>
            </div>
          }
          flush>
          <table className="mp-table mp-permtable">
            <thead>
              <tr>
                <th className="mp-permtable__mod">{t('permModule')}</th>
                {R.perms.map((p) => <th key={p} className="mp-permtable__act">{t(p)}</th>)}
              </tr>
            </thead>
            <tbody>
              {R.modules.map((m) => (
                <tr key={m}>
                  <td className="mp-permtable__mod mp-table__strong">{t(m)}</td>
                  {sel.matrix[m].map((v, i) => (
                    <td key={i} className="mp-permtable__act">
                      <PermCell key={sel.id + '-' + m + '-' + i} on={!!v} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      </div>
    </div>
  );
}
